import { useState } from "react";
import { doc, serverTimestamp, setDoc, Timestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getApp } from "firebase/app";
import { db } from "../../firebase";

import { STAFF_THEME, FONT } from "../../theme/staff";
const BRAND = { ...STAFF_THEME, bg: STAFF_THEME.dark, line: STAFF_THEME.raised };

const FIELD_LABELS = {
  name: "Name",
  company: "Company",
  email: "Email",
  phone: "Phone",
  inquiryEventDate: "Event date",
  preferredTime: "Preferred time",
  flexibleDate: "Flexible on date",
  location: "Location",
  indoorOutdoor: "Indoor / outdoor",
  attendance: "Expected attendance",
  services: "Services",
  guestExperience: "Guest experience",
  performanceFormat: "Performance format",
  budget: "Budget",
  clientNotes: "Client notes",
};

function fmtValue(key, value) {
  if (value == null || value === "") return null;
  if (key === "flexibleDate") return value ? "Yes" : null;
  if (key === "inquiryEventDate") {
    const d = new Date(value + "T00:00:00");
    if (isNaN(d)) return String(value);
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  }
  return String(value);
}

export default function QuickUpdateModal({ booking, onClose, onApplied }) {
  const [text, setText] = useState("");
  const [stage, setStage] = useState("input"); // input | preview | saving
  const [parsed, setParsed] = useState(null);
  const [overwrite, setOverwrite] = useState(false);
  const [err, setErr] = useState(null);

  const onParse = async () => {
    if (!text.trim()) return;
    setErr(null);
    setStage("parsing");
    try {
      const fn = httpsCallable(getFunctions(getApp()), "bookingQuickAddParse");
      const res = await fn({ text });
      setParsed(res.data.fields || {});
      setStage("preview");
    } catch (e) {
      setErr(e.message || "Parse failed");
      setStage("input");
    }
  };

  // Which fields would actually change given the overwrite setting.
  const plan = parsed ? Object.entries(parsed)
    .filter(([key, value]) => {
      if (value == null || value === "") return false;
      const existing = booking[key];
      const hasExisting = existing != null && existing !== "";
      if (hasExisting && !overwrite) return false;
      return true;
    }) : [];

  const skipped = parsed ? Object.entries(parsed)
    .filter(([key, value]) => {
      if (value == null || value === "") return false;
      const existing = booking[key];
      const hasExisting = existing != null && existing !== "";
      return hasExisting && !overwrite;
    }) : [];

  const onApply = async () => {
    if (plan.length === 0) { onClose(); return; }
    setStage("saving");
    setErr(null);
    try {
      const patch = {};
      for (const [key, value] of plan) {
        if (key === "inquiryEventDate" && value) {
          const d = new Date(value + "T00:00:00");
          patch[key] = isNaN(d) ? null : Timestamp.fromDate(d);
        } else {
          patch[key] = value;
        }
      }
      // Append the raw text to clientNotes unless we're overwriting it, to
      // preserve context for later.
      if (!("clientNotes" in patch) || !overwrite) {
        const existingNotes = booking.clientNotes || "";
        const divider = existingNotes ? "\n\n--- Quick update ---\n" : "";
        patch.clientNotes = existingNotes + divider + text.trim();
      }
      patch.updatedAt = serverTimestamp();
      await setDoc(doc(db, "bookings", booking.id), patch, { merge: true });
      onApplied?.();
      onClose();
    } catch (e) {
      setErr("Save failed: " + e.message);
      setStage("preview");
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.65)",
        zIndex: 50,
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "2rem 1rem",
        overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: BRAND.bg,
          border: `1px solid ${BRAND.line}`,
          borderRadius: "14px",
          padding: "1.1rem",
          maxWidth: "620px",
          width: "100%",
          color: BRAND.text,
          fontFamily: FONT.body,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.7rem" }}>
          <h2 style={{ margin: 0, fontSize: "1.15rem", color: BRAND.gold }}>⚡ Quick Update</h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent", border: "none", color: BRAND.muted,
              fontSize: "1.5rem", cursor: "pointer", lineHeight: 1,
            }}
            aria-label="Close"
          >×</button>
        </div>

        <p style={{ color: BRAND.muted, fontSize: "0.85rem", marginTop: 0, marginBottom: "0.8rem" }}>
          Paste new info about this booking (email reply, call notes, etc.). Claude will extract what it can and merge into empty fields only — your existing data is safe.
        </p>

        {err && (
          <div style={{ background: "#6b2234", color: "white", padding: "0.6rem 0.8rem", borderRadius: "8px", marginBottom: "0.6rem", fontSize: "0.85rem" }}>
            {err}
          </div>
        )}

        {(stage === "input" || stage === "parsing") && (
          <>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste email / notes / dictation here…"
              rows={10}
              disabled={stage === "parsing"}
              style={{
                width: "100%", background: BRAND.panel, border: `1px solid ${BRAND.line}`,
                borderRadius: "8px", color: BRAND.text, padding: "0.7rem",
                fontSize: "0.92rem", fontFamily: "inherit",
                outline: "none", resize: "vertical", boxSizing: "border-box",
              }}
            />
            <div style={{ marginTop: "0.7rem", display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={onParse}
                disabled={stage === "parsing" || !text.trim()}
                style={{
                  background: (stage === "parsing" || !text.trim()) ? BRAND.panel : BRAND.gold,
                  color: (stage === "parsing" || !text.trim()) ? BRAND.muted : BRAND.bg,
                  border: "none", borderRadius: "8px",
                  padding: "0.6rem 1.1rem", fontWeight: 800, fontSize: "0.9rem",
                  cursor: (stage === "parsing" || !text.trim()) ? "default" : "pointer",
                }}
              >
                {stage === "parsing" ? "Parsing…" : "✨ Extract fields"}
              </button>
            </div>
          </>
        )}

        {(stage === "preview" || stage === "saving") && parsed && (
          <>
            <div style={{
              background: BRAND.panel, border: `1px solid ${BRAND.line}`,
              borderRadius: "10px", padding: "0.8rem", marginBottom: "0.7rem",
            }}>
              <div style={{ color: BRAND.green, fontWeight: 700, fontSize: "0.78rem", marginBottom: "0.4rem", letterSpacing: "0.04em" }}>
                WILL UPDATE · {plan.length} field{plan.length === 1 ? "" : "s"}
              </div>
              {plan.length === 0 && (
                <div style={{ color: BRAND.muted, fontSize: "0.85rem" }}>
                  Nothing new to apply — either the text had no extractable fields, or all fields already have values (toggle overwrite below to replace them).
                </div>
              )}
              {plan.map(([key, value]) => (
                <div key={key} style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: "0.6rem", padding: "0.25rem 0", fontSize: "0.86rem" }}>
                  <div style={{ color: BRAND.muted }}>{FIELD_LABELS[key] || key}</div>
                  <div style={{ color: BRAND.cream, wordBreak: "break-word" }}>{fmtValue(key, value)}</div>
                </div>
              ))}
            </div>

            {skipped.length > 0 && (
              <div style={{
                background: BRAND.panel, border: `1px dashed ${BRAND.line}`,
                borderRadius: "10px", padding: "0.7rem", marginBottom: "0.7rem",
              }}>
                <div style={{ color: BRAND.muted, fontWeight: 700, fontSize: "0.76rem", marginBottom: "0.3rem", letterSpacing: "0.04em" }}>
                  SKIPPED (already has value) · {skipped.length}
                </div>
                {skipped.map(([key, value]) => (
                  <div key={key} style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: "0.6rem", padding: "0.2rem 0", fontSize: "0.8rem", color: BRAND.muted }}>
                    <div>{FIELD_LABELS[key] || key}</div>
                    <div style={{ textDecoration: "line-through" }}>{fmtValue(key, value)}</div>
                  </div>
                ))}
                <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", color: BRAND.cream, fontSize: "0.82rem", marginTop: "0.5rem" }}>
                  <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
                  Also overwrite existing values
                </label>
              </div>
            )}

            <div style={{ color: BRAND.muted, fontSize: "0.75rem", marginBottom: "0.6rem" }}>
              The original pasted text will be appended to Client Notes so nothing gets lost.
            </div>

            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => setStage("input")}
                disabled={stage === "saving"}
                style={{
                  background: "transparent", color: BRAND.cream,
                  border: `1px solid ${BRAND.line}`, borderRadius: "8px",
                  padding: "0.55rem 0.9rem", fontWeight: 600, fontSize: "0.85rem",
                  cursor: stage === "saving" ? "default" : "pointer",
                }}
              >
                ← Edit text
              </button>
              <button
                type="button"
                onClick={onApply}
                disabled={stage === "saving" || plan.length === 0}
                style={{
                  background: (stage === "saving" || plan.length === 0) ? BRAND.panel : BRAND.green,
                  color: (stage === "saving" || plan.length === 0) ? BRAND.muted : "white",
                  border: "none", borderRadius: "8px",
                  padding: "0.6rem 1.1rem", fontWeight: 800, fontSize: "0.9rem",
                  cursor: (stage === "saving" || plan.length === 0) ? "default" : "pointer",
                }}
              >
                {stage === "saving" ? "Applying…" : `Apply ${plan.length} update${plan.length === 1 ? "" : "s"}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
